import type { RefObject } from "react";
import { PhoneInput } from "../../../lib/PhoneInput.js";
import { DUPLICATE_PHONE_MESSAGE } from "../../../lib/emergencyContactRules.js";
import { Input } from "../../ui/input.js";
import { SelectField } from "../../ui/select-field.js";
import { Field, FieldGrid, CollapsibleSection } from "../sectionShared.js";

type InputProps = { value: string; onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; disabled?: boolean; style: React.CSSProperties };

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  inputProps: (field: string, value: string, onChange: (v: string) => void) => InputProps;
  /** Both contacts share one number — shown under Contact 2, the one to change. */
  duplicatePhone: boolean;
  ec1Name: string; setEc1Name: (v: string) => void;
  ec1Phone: string; setEc1Phone: (v: string) => void;
  ec1Relation: string; setEc1Relation: (v: string) => void;
  ec2Name: string; setEc2Name: (v: string) => void;
  ec2Phone: string; setEc2Phone: (v: string) => void;
  ec2Relation: string; setEc2Relation: (v: string) => void;
}

const RELATION_PRESETS = ["Spouse", "Parent", "Sibling", "Child", "Friend", "Relative", "Guardian"];

// inputProps() hands back the legacy inline `inp` / `disabledInp` style for the
// raw <input> call sites that still exist elsewhere. Input expresses the same
// look as Tailwind classes, so the style is dropped rather than layered on top
// of it -- keeping it would re-declare the border, radius and background the
// primitive already sets, and it carries `outline: none` with no focus rule,
// which is why these fields showed no focus state at all while the Relationship
// dropdown beside them did.
function withoutStyle<T extends { style?: React.CSSProperties }>(props: T): Omit<T, "style"> {
  const { style: _dropped, ...rest } = props;
  return rest;
}

// Matches disabledInp, which Input's own `disabled:opacity-50` does not
// reproduce, so masked and permission-locked fields keep the look they had.
const DISABLED_LOOK = "disabled:bg-surface-50 disabled:text-surface-600 disabled:opacity-100";

export function EmergencyContactsSection({
  sectionRef, inputProps, duplicatePhone,
  ec1Name, setEc1Name, ec1Phone, setEc1Phone, ec1Relation, setEc1Relation,
  ec2Name, setEc2Name, ec2Phone, setEc2Phone, ec2Relation, setEc2Relation,
}: Props) {
  return (
    <CollapsibleSection id="contacts" sectionRef={sectionRef} title="Emergency Contacts">
      <FieldGrid cols="two" gap="lg">
        {[
          { label: "Contact 1", prefix: "emergencyContact1", name: ec1Name, setName: setEc1Name, phone: ec1Phone, setPhone: setEc1Phone, relation: ec1Relation, setRelation: setEc1Relation },
          { label: "Contact 2", prefix: "emergencyContact2", name: ec2Name, setName: setEc2Name, phone: ec2Phone, setPhone: setEc2Phone, relation: ec2Relation, setRelation: setEc2Relation },
        ].map(({ label, prefix, name, setName, phone, setPhone, relation, setRelation }, idx) => {
          const phoneProps = inputProps(`${prefix}Phone`, phone, setPhone);
          return (
          <div key={label} style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            <p style={{ fontSize: "12px", fontWeight: 700, color: "var(--color-surface-700)", margin: 0 }}>{label}</p>
            <Field label="Full Name">
              <Input {...withoutStyle(inputProps(`${prefix}Name`, name, setName))} className={DISABLED_LOOK} placeholder="Full name" />
            </Field>
            <Field label="Phone">
              <PhoneInput value={phoneProps.value}
                onChange={(combined) => { if (!phoneProps.disabled) setPhone(combined); }}
                disabled={!!phoneProps.disabled} />
              {duplicatePhone && idx === 1 && (
                <p className="mt-1 text-xs text-[var(--color-error-fg)]">{DUPLICATE_PHONE_MESSAGE}</p>
              )}
            </Field>
            <Field label="Relationship">
              {(() => {
                const dropdownValue = relation === "" ? "" : (RELATION_PRESETS.includes(relation) ? relation : "Other");
                const showFree = relation === "Other" || (relation.length > 0 && !RELATION_PRESETS.includes(relation));
                const relationProps = inputProps(`${prefix}Relation`, relation, setRelation);
                return (
                  <>
                    <SelectField
                      value={dropdownValue}
                      onChange={e => { if (!relationProps.disabled) setRelation(e.target.value); }}
                      {...(relationProps.disabled !== undefined ? { disabled: relationProps.disabled } : {})}
                    >
                      <option value="">— Select —</option>
                      {RELATION_PRESETS.map(o => <option key={o} value={o}>{o}</option>)}
                      <option value="Other">Other</option>
                    </SelectField>
                    {showFree && (
                      <Input
                        value={relation === "Other" ? "" : relation}
                        onChange={e => { if (!relationProps.disabled) setRelation(e.target.value || "Other"); }}
                        disabled={relationProps.disabled}
                        className={`mt-1.5 ${DISABLED_LOOK}`}
                        placeholder="Specify relationship" autoFocus />
                    )}
                  </>
                );
              })()}
            </Field>
          </div>
          );
        })}
      </FieldGrid>
    </CollapsibleSection>
  );
}
